import { CircleX } from 'lucide-react';
import { Button, Dialog, Select } from '@platform/design-system';
import { useTranslation } from '@platform/i18n';
import { useState } from 'react';
import { CANCEL_REASONS, type CancelReason } from './orders.ts';
import styles from './Orders.module.css';

export type CancelOrderDialogProps = {
  title: string;
  notes: readonly string[];
  confirmLabel: string;
  keepLabel: string;
  busy: boolean;
  errorMessage: string | null;
  onCancel: (reason: CancelReason) => void;
  onKeep: () => void;
};

const reasonOf = (value: string): CancelReason =>
  CANCEL_REASONS.find((reason) => reason === value) ?? 'other';

export const CancelOrderDialog = ({
  title,
  notes,
  confirmLabel,
  keepLabel,
  busy,
  errorMessage,
  onCancel,
  onKeep,
}: CancelOrderDialogProps) => {
  const { t } = useTranslation();
  const [reason, setReason] = useState<CancelReason>(CANCEL_REASONS[0] ?? 'other');
  return (
    <Dialog
      open
      title={title}
      closeLabel={t('common.close')}
      onClose={onKeep}
      footer={
        <>
          <Button variant="ghost" onClick={onKeep}>
            {keepLabel}
          </Button>
          <Button
            icon={<CircleX size={20} aria-hidden="true" />}
            variant="danger"
            disabled={busy}
            onClick={() => {
              onCancel(reason);
            }}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      {notes.map((note) => (
        <p key={note}>{note}</p>
      ))}
      <Select
        label={t('orders.cancelReason')}
        value={reason}
        options={CANCEL_REASONS.map((value) => ({ value, label: t(`orders.reason_${value}`) }))}
        onChange={(event) => {
          setReason(reasonOf(event.target.value));
        }}
      />
      {errorMessage === null ? null : (
        <p className={styles['failure']} role="alert">
          {errorMessage}
        </p>
      )}
    </Dialog>
  );
};
